import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ============================================================================
 * ScrollManager — scroll behaviour for a client-rendered multi-route SPA.
 * ============================================================================
 *
 * Renders nothing. It exists because two things a static site gets for free
 * are broken in a client-rendered one:
 *
 * 1. **Route changes keep the old scroll position.** Following "All stories →"
 *    from the foot of the home page would land you at the foot of /stories.
 *
 * 2. **Native hash scrolling races React.** A section link on /stories points
 *    at `/#experience`, which is a real document load: the browser looks for
 *    `#experience` while the shell is still empty, because React has not
 *    mounted yet. The browser's own attempt therefore finds nothing. Running
 *    after mount — and after a frame, so layout has settled — is what makes
 *    cross-route anchors actually work.
 *
 * Same-page hash clicks do not reach this component: an `<a href="/#about">`
 * on "/" is a same-document hash change, which fires `hashchange` but not
 * `popstate`, and react-router's history listens for `popstate`. The browser
 * handles those natively, which is correct — this only covers the cases it
 * cannot.
 *
 * The whole problem disappears at the D-003 Vite/SSG migration, when the HTML
 * arrives with its sections already in it. Transitional by design.
 */
const ScrollManager = (): null => {
	const { pathname, hash } = useLocation();

	useEffect(() => {
		if (!hash) {
			window.scrollTo(0, 0);
			return;
		}

		const target = document.getElementById(hash.slice(1));
		if (!target) return;

		const frame = window.requestAnimationFrame(() => {
			target.scrollIntoView({ behavior: "smooth", block: "start" });
		});
		return () => window.cancelAnimationFrame(frame);
	}, [pathname, hash]);

	return null;
};

export default ScrollManager;
